import { ChangeDetectionStrategy, Component, computed, EventEmitter, Input, Output, signal } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { TranslocoModule } from '@jsverse/transloco';
import { NamePipe } from 'shared/util-common';

import { User } from '@treetop/shared/model';

import { CheckboxComponent } from '../../checkbox/checkbox.component';
import { IconDirective } from '../../icon/icon.directive';
import { InputDirective, InputPrefixDirective } from '../../input';

type SelectableUser = User & { selected: boolean };

@Component({
  selector: 'tt-guidelines-filter-popover-user-selector',
  standalone: true,
  imports: [
    MatButtonModule,
    TranslocoModule,
    CheckboxComponent,
    InputDirective,
    FormsModule,
    IconDirective,
    InputPrefixDirective,
    MatIconModule,
    ReactiveFormsModule,
    NamePipe,
  ],
  templateUrl: './filter-popover-user-selector.component.html',
  styleUrls: ['./filter-popover-user-selector.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SharedFilterPopoverUserSelectorComponent {
  private _users = signal<User[]>([]);
  @Input() public set users(value: User[]) {
    this._users.set(value);
  }

  private _selectedUsers = signal<User[]>([]);
  @Input() public set selectedUsers(selectedUsers: User[]) {
    this._selectedUsers.set(selectedUsers ?? []);
  }

  @Input() public title: string;
  @Input() public idPrefix: string;
  @Input() public hasSpaceInBetween: boolean;

  @Output() public selectedUsersChanged = new EventEmitter<User[]>();

  protected searchTerm = signal('');

  protected vm = computed<{ users: SelectableUser[] }>(() => ({
    users: this._users()
      .map((user) => ({
        ...user,
        selected: this._selectedUsers().some((selectedUser) => selectedUser.id === user.id),
      }))
      .filter((user) => {
        return (
          user.firstName.toLowerCase().includes(this.searchTerm().toLowerCase()) ||
          user.lastName.toLowerCase().includes(this.searchTerm().toLowerCase())
        );
      }),
  }));

  protected handleUserSelected(user: SelectableUser) {
    this._selectedUsers.update((selectedUsers) => {
      if (selectedUsers.some((selectedUser) => selectedUser.id === user.id)) {
        return selectedUsers.filter((selectedUser) => selectedUser.id !== user.id);
      }

      return [...selectedUsers, user];
    });

    this.selectedUsersChanged.emit(this._selectedUsers());
  }
}
